import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Sparkles, Plus, Trash2, CheckCircle2, Clock, ShieldCheck, ClipboardList } from "lucide-react";
import { rank } from "../../lib/rbac";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Housekeeping() {
  const [role, setRole] = useState("");
  const [userName, setUserName] = useState("");
  const [tasks, setTasks] = useState([]);
  const [reports, setReports] = useState([]);
  const [hours, setHours] = useState([]);
  const [newTask, setNewTask] = useState("");
  const [shift, setShift] = useState("Morning");
  const [checked, setChecked] = useState({});
  const [notes, setNotes] = useState("");
  const [hoursForm, setHoursForm] = useState({ open_time: "", close_time: "" });

  const isManager = rank(role) >= 60;
  const canVerify = rank(role) >= 40;
  const canEditChecklist = rank(role) >= 60;

  const fetchAll = useCallback(async () => {
    try {
      const [me, t, r, h] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/auth/me`),
        axios.get(`${BACKEND_URL}/api/cleaning/tasks`),
        axios.get(`${BACKEND_URL}/api/cleaning/reports`),
        axios.get(`${BACKEND_URL}/api/gym-hours`),
      ]);
      setRole(me.data?.user?.role || ""); setUserName(me.data?.user?.name || "");
      setTasks(t.data); setReports(r.data); setHours(h.data);
    } catch (e) { console.error(e); }
  }, []);
  useEffect(() => { fetchAll(); }, [fetchAll]);

  const activeTasks = tasks.filter(t => t.active !== false);

  const addTask = async (e) => { e.preventDefault(); if (!newTask.trim()) return; try { await axios.post(`${BACKEND_URL}/api/cleaning/tasks`, { name: newTask.trim() }); setNewTask(""); fetchAll(); } catch (err) { alert(err.response?.data?.detail || "Only Branch Manager and above can edit the checklist."); } };
  const delTask = async (id) => { try { await axios.delete(`${BACKEND_URL}/api/cleaning/tasks/${id}`); fetchAll(); } catch (err) { alert(err.response?.data?.detail || "Only Branch Manager and above can edit the checklist."); } };

  const submitReport = async (e) => {
    e.preventDefault();
    const items = activeTasks.map(t => ({ task: t.name, done: !!checked[t.id] }));
    await axios.post(`${BACKEND_URL}/api/cleaning/reports`, { security_name: userName, shift, items, notes });
    setChecked({}); setNotes(""); fetchAll();
  };

  const verify = async (id) => { await axios.post(`${BACKEND_URL}/api/cleaning/reports/${id}/verify`, { verified_by: userName }); fetchAll(); };

  const saveHours = async (e) => { e.preventDefault(); await axios.post(`${BACKEND_URL}/api/gym-hours`, { ...hoursForm, recorded_by: userName }); setHoursForm({ open_time: "", close_time: "" }); fetchAll(); };

  const inp = "bg-main_bg border border-border_soft rounded-xl p-3 text-text_primary text-sm focus:ring-1 focus:ring-primary/50";

  return (
    <div data-testid="housekeeping-page" className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
      <div>
        <h1 className="text-4xl font-serif text-text_primary mb-2">Security & Housekeeping</h1>
        <p className="text-text_secondary text-sm">Opening/closing times and daily cleaning reports.</p>
      </div>

      {/* Gym hours */}
      <div className="bg-card_bg border border-border_soft rounded-xl p-6">
        <h3 className="text-base font-semibold text-text_primary mb-4 flex items-center gap-2"><Clock size={18} className="text-gold" /> Opening & Closing Time (Today)</h3>
        <form onSubmit={saveHours} className="flex flex-col md:flex-row gap-3 mb-5">
          <div><label className="text-[11px] uppercase tracking-wider text-text_secondary font-semibold block mb-1">Open Time</label><input data-testid="open-time" type="time" value={hoursForm.open_time} onChange={e => setHoursForm({ ...hoursForm, open_time: e.target.value })} className={inp} /></div>
          <div><label className="text-[11px] uppercase tracking-wider text-text_secondary font-semibold block mb-1">Close Time</label><input data-testid="close-time" type="time" value={hoursForm.close_time} onChange={e => setHoursForm({ ...hoursForm, close_time: e.target.value })} className={inp} /></div>
          <button data-testid="save-hours-btn" type="submit" className="bg-primary text-[#111827] px-6 rounded-xl font-bold text-sm self-end py-3 hover:bg-primary_hover transition-colors">Save</button>
        </form>
        <div className="space-y-2 max-h-40 overflow-y-auto custom-scrollbar">
          {hours.map((h, i) => <div key={i} className="flex justify-between text-sm bg-main_bg border border-border_soft/60 rounded-lg px-4 py-2"><span className="text-text_secondary">{h.date}</span><span className="text-text_primary">Open {h.open_time || "-"} · Close {h.close_time || "-"}</span></div>)}
          {hours.length === 0 && <p className="text-sm text-text_secondary/70 italic">No timings logged yet.</p>}
        </div>
      </div>

      {/* Checklist manager (Branch Manager and above) */}
      {canEditChecklist && (
        <div className="bg-card_bg border border-border_soft rounded-xl p-6">
          <h3 className="text-base font-semibold text-text_primary mb-4 flex items-center gap-2"><ClipboardList size={18} className="text-gold" /> Cleaning Checklist (edit tasks)</h3>
          <div className="space-y-2 mb-5">
            {activeTasks.map(t => <div key={t.id} className="flex justify-between items-center bg-main_bg border border-border_soft/60 rounded-lg px-4 py-2.5"><span className="text-sm text-text_primary">{t.name}</span><button data-testid="del-task-btn" onClick={() => delTask(t.id)} className="text-text_secondary hover:text-accent_danger"><Trash2 size={15} /></button></div>)}
          </div>
          <form onSubmit={addTask} className="flex gap-3">
            <input data-testid="new-task-input" value={newTask} onChange={e => setNewTask(e.target.value)} placeholder="Add a cleaning task..." className={`flex-1 ${inp}`} />
            <button data-testid="add-task-btn" type="submit" className="bg-primary text-[#111827] px-5 rounded-xl font-bold text-sm flex items-center gap-2 hover:bg-primary_hover transition-colors"><Plus size={16} /> Add</button>
          </form>
        </div>
      )}

      {/* Submit report */}
      <div className="bg-card_bg border border-border_soft rounded-xl p-6">
        <h3 className="text-base font-semibold text-text_primary mb-4 flex items-center gap-2"><Sparkles size={18} className="text-gold" /> Submit Cleaning Report</h3>
        <form onSubmit={submitReport} className="space-y-4">
          <div className="flex gap-3 items-center">
            <label className="text-sm text-text_secondary">Shift:</label>
            <select data-testid="shift-select" value={shift} onChange={e => setShift(e.target.value)} className={inp}>
              {["Morning", "Afternoon", "Evening", "Night"].map(s => <option key={s}>{s}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {activeTasks.map(t => (
              <label key={t.id} className="flex items-center gap-2 bg-main_bg border border-border_soft rounded-xl px-3 py-2.5 cursor-pointer">
                <input type="checkbox" checked={!!checked[t.id]} onChange={e => setChecked({ ...checked, [t.id]: e.target.checked })} className="accent-gold w-4 h-4" />
                <span className="text-sm text-text_primary">{t.name}</span>
              </label>
            ))}
          </div>
          <textarea value={notes} onChange={e => setNotes(e.target.value)} placeholder="Notes (optional)" className={`w-full ${inp}`} rows={2} />
          <button data-testid="submit-report-btn" type="submit" className="bg-primary text-[#111827] px-6 py-3 rounded-xl font-bold text-sm hover:bg-primary_hover transition-colors">Submit Report</button>
        </form>
      </div>

      {/* Reports list */}
      <div className="bg-card_bg border border-border_soft rounded-xl p-6">
        <h3 className="text-base font-semibold text-text_primary mb-4 flex items-center gap-2"><ShieldCheck size={18} className="text-gold" /> Cleaning Reports</h3>
        <div className="space-y-2 max-h-96 overflow-y-auto custom-scrollbar">
          {reports.length === 0 && <p className="text-sm text-text_secondary/70 italic">No reports yet.</p>}
          {reports.map(r => {
            const done = (r.items || []).filter(i => i.done).length;
            const total = (r.items || []).length;
            return (
              <div key={r.id} data-testid="report-row" className="flex justify-between items-center bg-main_bg border border-border_soft/60 rounded-xl px-4 py-3">
                <div>
                  <p className="text-sm font-semibold text-text_primary">{r.date} · {r.shift} <span className="text-text_secondary font-normal">by {r.security_name || "Staff"}</span></p>
                  <p className="text-xs text-text_secondary">{done}/{total} tasks done{r.notes ? ` · ${r.notes}` : ""}</p>
                </div>
                <div className="flex items-center gap-3">
                  <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${r.status === "Verified" ? "bg-accent_success/15 text-accent_success" : "bg-accent_warning/15 text-accent_warning"}`}>{r.status}</span>
                  {canVerify && r.status !== "Verified" && <button data-testid="verify-report-btn" onClick={() => verify(r.id)} className="text-xs font-bold text-gold hover:text-primary_hover flex items-center gap-1"><CheckCircle2 size={14} /> Verify</button>}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
